/**
 * Centralized scroll utilities for consistent navigation behavior.
 * Ensures all page loads and anchor scrolls work identically on mobile/desktop.
 */

// Header height constants for offset calculations
const HEADER_HEIGHT_MOBILE = 64;
const HEADER_HEIGHT_DESKTOP = 72;
const SCROLL_PADDING = 16;

/**
 * Get the current header offset based on viewport width.
 */
export function getHeaderOffset(): number {
  if (typeof window === 'undefined') return HEADER_HEIGHT_DESKTOP;
  return window.innerWidth < 768 ? HEADER_HEIGHT_MOBILE : HEADER_HEIGHT_DESKTOP;
}

/**
 * Scroll to the top of the page instantly.
 * Used on route changes to ensure new pages start from the top.
 */
export function scrollToTop(behavior: ScrollBehavior = 'instant'): void {
  if (typeof window === 'undefined') return;
  
  window.scrollTo({
    top: 0,
    left: 0,
    behavior,
  });
}

/**
 * Scroll to a specific section by ID with smooth animation.
 * Accounts for sticky header offset.
 * 
 * @param sectionId - The HTML id of the target element (without #)
 * @param options - Optional configuration
 */
export function scrollToSection(
  sectionId: string,
  options: {
    behavior?: ScrollBehavior;
    offset?: number;
  } = {}
): void {
  if (typeof window === 'undefined') return;
  
  const { behavior = 'smooth', offset } = options;
  const element = document.getElementById(sectionId);
  
  if (!element) {
    console.warn(`[scrollToSection] Element with id "${sectionId}" not found`);
    return;
  }
  
  // Calculate position accounting for header
  const headerOffset = offset ?? getHeaderOffset() + SCROLL_PADDING;
  const elementPosition = element.getBoundingClientRect().top + window.scrollY;
  const targetPosition = Math.max(0, elementPosition - headerOffset);
  
  window.scrollTo({
    top: targetPosition,
    behavior,
  });
}

/**
 * Disable browser's automatic scroll restoration.
 * Call this once on app initialization.
 */
export function disableScrollRestoration(): void {
  if (typeof window === 'undefined') return;
  
  if ('scrollRestoration' in window.history) {
    window.history.scrollRestoration = 'manual';
  }
}

/**
 * Handle hash-based navigation (e.g., /page#section).
 * Waits for the page to mount before scrolling.
 */
export function handleHashNavigation(): void {
  if (typeof window === 'undefined') return;
  
  const hash = window.location.hash;
  if (hash) {
    const sectionId = hash.replace('#', '');
    // Wait for next frame to ensure DOM is ready
    requestAnimationFrame(() => {
      // Additional delay for React to finish rendering
      setTimeout(() => {
        scrollToSection(sectionId);
      }, 100);
    });
  }
}

/**
 * Create a click handler for anchor links that smoothly scrolls to sections.
 */
export function createAnchorClickHandler(sectionId: string): (e: React.MouseEvent) => void {
  return (e: React.MouseEvent) => {
    e.preventDefault();
    scrollToSection(sectionId);
    // Update URL hash without triggering navigation
    window.history.pushState(null, '', `#${sectionId}`);
  };
}
